/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { CheckOutlined, CopyOutlined, SearchOutlined } from '@ant-design/icons';
import { Input, Spin } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';

import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useApiSchemaQuery } from '@/hooks/query/useApiSchemaQuery';
import { Tooltip } from '@/primitives/Tooltip';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import { EndpointDetail } from './EndpointDetail';
import { EndpointList } from './EndpointList';
import { resolveSchema } from './schemaUtils';
import { Endpoint, ParsedSpec } from './types';

import './ApiReferencePage.css';

const HTTP_METHODS = ['get', 'post', 'put', 'patch', 'delete'] as const;

function parseSpec(spec: Record<string, unknown>): ParsedSpec {
  const paths = spec.paths as Record<string, Record<string, Record<string, unknown>>>;
  const schemas = ((spec.components as Record<string, unknown>)?.schemas as Record<string, unknown>) || {};
  const tagMap = new Map<string, Endpoint[]>();

  Object.entries(paths || {}).forEach(([path, pathObj]) => {
    HTTP_METHODS.forEach((method) => {
      const op = pathObj[method];
      if (!op) return;

      const tags = (op.tags as string[]) || ['Other'];
      const endpoint: Endpoint = {
        method,
        path,
        operationId: op.operationId as string,
        summary: op.summary as string,
        description: op.description as string,
        permission: op['x-required-permission'] as string,
        parameters: op.parameters as Endpoint['parameters'],
        requestBody: op.requestBody as Endpoint['requestBody'],
        responses: op.responses as Endpoint['responses'],
      };

      tags.forEach((tag) => {
        if (!tagMap.has(tag)) tagMap.set(tag, []);
        tagMap.get(tag)!.push(endpoint);
      });
    });
  });

  const parsedTags = [...tagMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, endpoints]) => ({ name, endpoints }));

  return { tags: parsedTags, schemas };
}

export function ApiReferencePage() {
  const { data: spec, isLoading, error } = useApiSchemaQuery();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Endpoint | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [tokenCopied, setTokenCopied] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const subtenantID = useMgmtPlaneStateStore((state) => state.selectedSubtenantID);

  const [navWidth, setNavWidth] = useState(340);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const handleResizeStart = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = navWidth;
      dragRef.current = { startX, startWidth };
      setIsDragging(true);

      const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
        if (!dragRef.current) return;
        const delta = moveEvent.clientX - dragRef.current.startX;
        const newWidth = Math.max(260, Math.min(700, dragRef.current.startWidth + delta));
        setNavWidth(newWidth);
      };

      const handleMouseUp = () => {
        dragRef.current = null;
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [navWidth],
  );

  useEffect(() => {
    const outlet = document.querySelector('.main-layout-outlet') as HTMLElement | null;
    const content = outlet?.parentElement as HTMLElement | null;

    const prevOutletOverflow = outlet?.style.overflow ?? '';
    const prevOutletMinHeight = outlet?.style.minHeight ?? '';
    const prevContentOverflow = content?.style.overflow ?? '';

    if (outlet) {
      outlet.style.overflow = 'hidden';
      outlet.style.minHeight = '0';
    }

    if (content) {
      content.style.overflow = 'hidden';
    }

    return () => {
      if (outlet) {
        outlet.style.overflow = prevOutletOverflow;
        outlet.style.minHeight = prevOutletMinHeight;
      }

      if (content) {
        content.style.overflow = prevContentOverflow;
      }
    };
  }, []);

  useEffect(() => {
    mgmtPlaneAPIClient.auth
      .generateToken()
      .then((res) => {
        setAuthToken(res.data.token);
      })
      .catch(() => {
        setAuthToken(null);
      })
      .finally(() => {
        setAuthLoading(false);
      });
  }, []);

  const parsed = useMemo(() => (spec ? parseSpec(spec as unknown as Record<string, unknown>) : null), [spec]);

  const filtered = useMemo(() => {
    if (!parsed) return [];
    const q = search.toLowerCase();
    if (!q) return parsed.tags;
    return parsed.tags
      .map((tag) => ({
        ...tag,
        endpoints: tag.endpoints.filter(
          (ep) =>
            ep.path.toLowerCase().includes(q) ||
            ep.operationId?.toLowerCase().includes(q) ||
            ep.description?.toLowerCase().includes(q) ||
            ep.permission?.toLowerCase().includes(q) ||
            ep.method.toLowerCase().includes(q),
        ),
      }))
      .filter((tag) => tag.endpoints.length > 0);
  }, [parsed, search]);

  const resolver = useCallback(
    (schema: Record<string, unknown>) => resolveSchema(schema, parsed?.schemas || {}),
    [parsed],
  );

  const handleSelect = useCallback((ep: Endpoint | null) => {
    setSelected(ep);
    contentRef.current?.scrollTo(0, 0);
  }, []);

  const handleCopyToken = () => {
    if (!authToken) return;

    navigator.clipboard.writeText(authToken).then(
      () => {
        setTokenCopied(true);
        setTimeout(() => setTokenCopied(false), 2000);
      },
      () => {},
    );
  };

  if (isLoading) {
    return (
      <div className="apiref">
        <div className="apiref-loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error || !parsed) {
    return (
      <div className="apiref">
        <div className="apiref-loading">
          <span className="apiref-error">Unable to load API schema</span>
        </div>
      </div>
    );
  }

  return (
    <div className="apiref">
      <div className="apiref-nav-wrapper" style={{ width: navWidth }}>
        <div className="apiref-nav">
          <div className="apiref-nav-header">
            <Input
              prefix={<SearchOutlined style={{ opacity: 0.3 }} />}
              placeholder="Search endpoints..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              variant="borderless"
              className="apiref-search-input"
            />
          </div>
          <div className="apiref-nav-list">
            <EndpointList tags={filtered} selected={selected} onSelect={handleSelect} />
          </div>
          <div className="apiref-nav-token">
            {authLoading && <span className="apiref-nav-token-text">Generating token...</span>}
            {!authLoading && !authToken && <span className="apiref-nav-token-text error">Auth failed</span>}
            {!authLoading && authToken && (
              <>
                <span className="apiref-nav-token-label">Session Token</span>
                <Tooltip title="This token is valid for 24 hours">
                  <code className="apiref-nav-token-value">{authToken}</code>
                </Tooltip>
                <button
                  type="button"
                  className="apiref-nav-token-copy"
                  onClick={handleCopyToken}
                  aria-label="Copy session token"
                >
                  {tokenCopied ? <CheckOutlined style={{ color: '#27ae60' }} /> : <CopyOutlined />}
                </button>
              </>
            )}
          </div>
        </div>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div className={`apiref-nav-resize ${isDragging ? 'is-dragging' : ''}`} onMouseDown={handleResizeStart} />
      </div>
      <div className="apiref-content" ref={contentRef}>
        {selected ? (
          <EndpointDetail
            endpoint={selected}
            schemas={parsed?.schemas || {}}
            resolveSchema={resolver}
            authToken={authToken}
            tenantID={tenantID}
            subtenantID={subtenantID}
          />
        ) : (
          <div className="apiref-welcome">
            <div className="apiref-welcome-inner">
              <span className="apiref-welcome-icon">&#123; &#125;</span>
              <h2>API Reference</h2>
              <p>
                Select an endpoint from the sidebar to explore request parameters, response schemas, and permissions.
              </p>
              <div className="apiref-welcome-stats">
                <div className="apiref-stat">
                  <span className="apiref-stat-value">{parsed.tags.reduce((n, t) => n + t.endpoints.length, 0)}</span>
                  <span className="apiref-stat-label">Endpoints</span>
                </div>
                <div className="apiref-stat">
                  <span className="apiref-stat-value">{parsed.tags.length}</span>
                  <span className="apiref-stat-label">Categories</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
